import { Text } from '@consta/uikit/Text'
import cn from 'classnames'
import type { Nullable } from 'common/types'
import type { FC, PropsWithChildren, ReactNode, RefObject } from 'react'
import { useRef } from 'react'

import { useAutoResize, useBooleanFlag } from '../../../../hooks'
import { AnimatedPopover } from '../../../AnimatedPopover'
import { ExpandableContent } from '../../../ExpandableContent'
import type { HeaderBlockPosition } from '../HeaderBlocks'
import { internalClassName, titleClassName } from './constants'
import css from './HeaderExpandablePopover.css'

type HeaderExpandablePopoverProps = {
  className?: string
  title: string
  anchorRef: RefObject<HTMLElement>
  initialExpanded?: boolean
  isOpen: boolean
  position: HeaderBlockPosition
  maxHeight?: number
  headerSlot?: ReactNode
  onToggleExpanded?: (isExpanded: boolean) => void
}

export const HeaderExpandablePopover: FC<PropsWithChildren<HeaderExpandablePopoverProps>> = ({
  className,
  children,
  title,
  anchorRef,
  isOpen,
  position,
  maxHeight,
  headerSlot,
  initialExpanded = false,
  onToggleExpanded,
}) => {
  const [expanded, { toggle: toggleExpanded }] = useBooleanFlag(initialExpanded)
  const headerRef = useRef<Nullable<HTMLElement>>(null)
  const { height: headerHight } = useAutoResize(headerRef.current)

  const handleToggle = () => {
    toggleExpanded()
    onToggleExpanded?.(!expanded)
  }

  const direction = placePopover(position)

  const style = makeHeightStyle(maxHeight, headerHight)

  return (
    <AnimatedPopover
      className={cn(
        internalClassName,
        {
          [css.left]: position === 'left',
          [css.right]: position === 'right',
          [css.center]: position === 'center',
        },
        className,
      )}
      direction={direction}
      offset="s"
      anchorRef={anchorRef}
      isOpen={isOpen}
      style={style}>
      <div className={css.body}>
        <header ref={headerRef} className={titleClassName}>
          <Text as="h1" size="s" view="primary">
            {title}
          </Text>
          {headerSlot}
        </header>
        <ExpandableContent expanded={expanded} onExpandClick={handleToggle}>
          {children}
        </ExpandableContent>
      </div>
    </AnimatedPopover>
  )
}

const placePopover = (position: HeaderBlockPosition) => {
  switch (position) {
    case 'left':
      return 'downStartLeft'
    case 'center':
      return 'downCenter'
    case 'right':
      return 'downStartRight'
  }
}

function makeHeightStyle(
  maxHeight: number | undefined,
  headerHeight: number,
): Record<string, string> | undefined {
  if (!maxHeight) return

  const calculatedHeight = maxHeight - headerHeight

  return {
    '--header-popover-expanded-height': `${calculatedHeight}px`,
    '--header-title-height': `${headerHeight}px`,
  }
}
